import { PermissionGuard } from '@/components/PermissionGuard';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import type { PhonePlan } from '@octave/contracts';
import { Phone, ShoppingCart, Wifi } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDataAllowance, formatPrice } from '@/lib/format';

const operatorImages: Record<string, string> = {
  Orange: '/images/operators/orange.webp',
  SFR: '/images/operators/sfr.svg',
  Bouygues: '/images/operators/bouygues.png',
};


interface PlanDetailsDialogProps {
  plan: PhonePlan | null;
  onClose: () => void;
}

export function PlanDetailsDialog({ plan, onClose }: PlanDetailsDialogProps) {
  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();

  const handleOrder = () => {
    if (!plan) return;
    const params = new URLSearchParams({
      operatorId: plan.operator.id,
      planId: plan.id,
      mobileLineType: plan.isDataOnly ? 'dataonly' : 'classic',
    });
    navigate(`/o/${tenantSlug}/lines/new?${params.toString()}`);
  };

  return (
    <Dialog open={!!plan} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="!max-w-lg p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            {plan && (
              <img
                src={
                  operatorImages[plan.operator.name] ||
                  '/images/operators/default.svg'
                }
                alt={plan.operator.name}
                className="h-10 w-10 object-contain"
              />
            )}
            <div>
              <DialogTitle className="text-lg font-bold text-slate-900">
                {plan?.name}
              </DialogTitle>
              <p className="text-sm text-slate-500">{plan?.operator.name}</p>
            </div>
          </div>
        </DialogHeader>

        {plan && (
          <div className="px-6 py-6 space-y-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  {plan.isDataOnly ? (
                    <Wifi className="h-4 w-4" />
                  ) : (
                    <Phone className="h-4 w-4" />
                  )}
                  <p className="text-xs font-medium">Type</p>
                </div>
                <p className="text-sm font-semibold text-slate-900">
                  {plan.isDataOnly ? 'SIM Data' : 'Ligne mobile'}
                </p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs font-medium text-slate-500 mb-1">
                  Enveloppe data
                </p>
                <p className="text-sm font-semibold text-slate-900">
                  {formatDataAllowance(plan.dataLimitMo)}
                </p>
              </div>
            </div>

            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 flex items-end justify-between">
              <div>
                <p className="text-xs font-medium text-emerald-700">
                  Prix mensuel
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {formatPrice(plan.price)}
                  <span className="text-sm font-normal text-slate-500">
                    {' '}
                    /mois
                  </span>
                </p>
              </div>
            </div>

            <PermissionGuard permission="write">
              <Button
                onClick={handleOrder}
                className="w-full h-11 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-sm hover:shadow-md transition-all"
              >
                <ShoppingCart className="h-4 w-4" />
                Commander une ligne avec ce forfait
              </Button>
            </PermissionGuard>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
